import React, { useEffect, useState } from 'react';
import APIService from '../services/api';
import { useCleLecture } from '../services/cleLecture';
import { photoSrc, initiales } from './AvatarCandidat';
import '../da-vue.css';
import './CarteProfil.css';

/**
 * LA CARTE PROFIL — le one pager d'un candidat (maquette 01.11).
 *
 * Deux cartes (01.11) : la GÉNÉRALE à l'ouverture (compétences du CV et
 * durée de pratique, aucun score) et celle d'un BESOIN choisi parmi ses
 * positionnements (adéquation, exigences du barème). Tout vient de
 * `GET /candidates/:id/carte-profil[?besoin_id=]`.
 * L'écran ne calcule rien : sans score, il répète le message du serveur.
 * Les boutons d'envoi (PDF, partage, client) n'ont pas encore de route :
 * ils ne sont pas affichés.
 */
const BASE = 'https://besoins-tat-production.up.railway.app';

const ICONE_FAMILLE = {
  primordiales: 'bi-shield-check',
  importantes: 'bi-layers',
  formation: 'bi-mortarboard',
  optionnelles: 'bi-plus-circle',
  missions: 'bi-briefcase',
  domaine: 'bi-diagram-3',
  technologies: 'bi-tools',
  liste: 'bi-list-check',
};

// D'où viennent les missions affichées (le serveur le dit, l'écran le nomme).
const SOURCES_CV = {
  cv_format_entreprise: 'CV format entreprise',
  cv_de_la_fiche: 'CV de la fiche',
  cv_du_besoin: 'CV du besoin',
};

const fmtMoisAn = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Jauge en arc (270°) — la part est le score du besoin, rien d'autre. */
function JaugeArc({ valeur }) {
  const r = 46; const c = 2 * Math.PI * r; const arc = c * 0.75;
  const part = Math.max(0, Math.min(100, valeur || 0)) / 100;
  return (
    <svg className="cp-jauge" viewBox="0 0 120 120" role="img" aria-label={`Adéquation au besoin : ${valeur} %`}>
      <circle cx="60" cy="60" r={r} className="cp-jauge-piste" strokeDasharray={`${arc} ${c}`} transform="rotate(135 60 60)" />
      <circle cx="60" cy="60" r={r} className="cp-jauge-part" strokeDasharray={`${arc * part} ${c}`} transform="rotate(135 60 60)" />
      <text x="60" y="62" textAnchor="middle" className="cp-jauge-valeur">{valeur}<tspan className="cp-jauge-pct"> %</tspan></text>
      <text x="60" y="80" textAnchor="middle" className="cp-jauge-legende">Adéquation</text>
    </svg>
  );
}

/** Radar de l'univers de compétences : un axe par exigence du barème. */
function Radar({ axes }) {
  const n = axes.length;
  const R = 112; const cx = 160; const cy = 150;
  const pt = (i, v) => {
    const a = -Math.PI / 2 + (2 * Math.PI * i) / n;
    return [cx + Math.cos(a) * R * v, cy + Math.sin(a) * R * v];
  };
  const anneau = (v) => axes.map((_, i) => pt(i, v).join(',')).join(' ');
  const forme = axes.map((a, i) => pt(i, Math.max(0.04, a.niveau / 100)).join(',')).join(' ');
  return (
    <div className="cp-radar">
      <svg viewBox="0 0 320 300" role="img" aria-label="Univers de compétences">
        {[0.5, 0.7, 0.85, 1].map(v => <polygon key={v} points={anneau(v)} className="cp-radar-anneau" />)}
        {axes.map((_, i) => { const [x, y] = pt(i, 1); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} className="cp-radar-rayon" />; })}
        <polygon points={forme} className="cp-radar-forme" />
        {axes.map((a, i) => { const [x, y] = pt(i, Math.max(0.04, a.niveau / 100)); return <circle key={i} cx={x} cy={y} r="3.5" className="cp-radar-point" />; })}
      </svg>
      {axes.map((a, i) => {
        const [x, y] = pt(i, 1.22);
        const cote = x < cx - 8 ? 'gauche' : x > cx + 8 ? 'droite' : 'centre';
        return (
          <div key={i} className={`cp-radar-axe cp-radar-axe--${cote}`} style={{ left: `${(x / 320) * 100}%`, top: `${(y / 300) * 100}%` }} title={a.exigence}>
            <i className={`bi ${ICONE_FAMILLE[a.famille] || 'bi-dot'}`} aria-hidden="true" />
            <span className="cp-radar-libelle">{a.libelle}{(a.fidelite_absents || []).length > 0 && <i className="bi bi-exclamation-triangle cp-infidele" title={`Absent du CV validé : ${a.fidelite_absents.join(', ')} — exclu du PDF client`} aria-label="absent du CV validé" />}</span>
            <span className={`cp-palier cp-palier--${a.palier}`}>{a.palier_libelle} · {a.valeur_lisible ?? a.niveau}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function CarteProfil({ candidatId, onDetail }) {
  useCleLecture();
  const [besoinId, setBesoinId] = useState(null);
  const [rechargement, setRechargement] = useState(0);
  const [etat, setEtat] = useState({ charge: true, data: null, erreur: null });
  const [gen, setGen] = useState({ en_cours: false, erreur: null });

  // La carte générale se construit AU CLIC : Leman lit le CV (crédits IA) ;
  // le serveur rend la carte à jour, l'écran la remplace telle quelle.
  // Carte d'un besoin : « pourquoi ce profil pour ce poste » (même route, besoin_id).
  const generer = async () => {
    setGen({ en_cours: true, erreur: null });
    try {
      const bid = etat.data?.mode === 'besoin' ? etat.data?.besoin?.besoin_id : null;
      const r = await fetch(`${BASE}/candidates/${candidatId}/carte-profil/generer`, {
        method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(bid ? { besoin_id: bid } : {}) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 && !j.error) { setGen({ en_cours: false, erreur: 'La génération de la carte arrive côté serveur.' }); return; }
      if (!r.ok) { setGen({ en_cours: false, erreur: j.error || `Erreur ${r.status}` }); return; }
      setEtat({ charge: false, data: j.data, erreur: null });
      setGen({ en_cours: false, erreur: null });
    } catch {
      setGen({ en_cours: false, erreur: 'Le serveur ne répond pas. Réessayez dans un instant.' });
    }
  };
  const [photoCassee, setPhotoCassee] = useState(false);

  // Export PDF pour le client : un document EXTERNE. La photo y entre quand
  // la fiche en a une — case COCHÉE par défaut (02.11 : « toujours pas de
  // photo » — la case partait décochée à chaque export) ; on la décoche si le
  // candidat refuse. Le nom est « Prénom N. » par défaut. Téléchargé avec l'en-tête Authorization, jamais un jeton
  // dans l'adresse.
  const [exp, setExp] = useState({ ouvert: false, photo: null, nom: 'complet', adequation: true, en_cours: false, erreur: null });
  // En carte d'un besoin, le nom suit UNE règle : celle du besoin (réglée
  // ici, elle vaut pour tous ses exports). En carte générale, l'export choisit.
  const choisirNom = async (mode) => {
    if (etat.data?.mode !== 'besoin') { setExp(x => ({ ...x, nom: mode })); return; }
    const bid = etat.data?.besoin?.besoin_id;
    try {
      const r = await fetch(`${BASE}/besoins/${bid}/nom-profils`, { method: 'PUT', headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ mode }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setExp(x => ({ ...x, erreur: r.status === 404 && !j.error ? 'Ce réglage arrive côté serveur.' : (j.error || `Erreur ${r.status}`) })); return; }
      setEtat(e => ({ ...e, data: { ...e.data, besoin: { ...e.data.besoin, nom_profils: mode } } }));
    } catch { setExp(x => ({ ...x, erreur: 'Le serveur ne répond pas. Réessayez dans un instant.' })); }
  };

  // « Recalculer le score » : le matching NORMAL du lot (gratuit à matière
  // inchangée) ; la carte se relit quand il a eu le temps de tourner.
  const [recalcul, setRecalcul] = useState({ en_cours: false, message: null });
  const recalculer = async () => {
    const aoId = etat.data?.besoin?.ao_id, bid = etat.data?.besoin?.besoin_id;
    if (!aoId) return;
    setRecalcul({ en_cours: true, message: null });
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/matching/recompute?besoin_id=${bid}`, { method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setRecalcul({ en_cours: false, message: j.error || `Erreur ${r.status}` }); return; }
      setRecalcul({ en_cours: true, message: 'Recalcul lancé : la carte se met à jour dans un instant.' });
      [20000, 60000].forEach(ms => setTimeout(() => setRechargement(n => n + 1), ms));
      setTimeout(() => setRecalcul({ en_cours: false, message: null }), 61000);
    } catch { setRecalcul({ en_cours: false, message: 'Le serveur ne répond pas. Réessayez dans un instant.' }); }
  };

  // null = pas encore touchée : cochée dès que la fiche a une photo.
  const photoExport = exp.photo ?? !!etat.data?.identite?.photo_preview_url;
  const exporter = async () => {
    setExp(x => ({ ...x, en_cours: true, erreur: null, info: null }));
    const q = new URLSearchParams({ download: '1', nom: etat.data?.mode === 'besoin' ? (etat.data?.besoin?.nom_profils || 'complet') : exp.nom, ...(photoExport ? { photo: '1' } : {}), ...(exp.adequation ? {} : { adequation: '0' }),
      ...(etat.data?.mode === 'besoin' && etat.data?.besoin?.besoin_id ? { besoin_id: String(etat.data.besoin.besoin_id) } : {}) });
    try {
      const r = await fetch(`${BASE}/candidates/${candidatId}/carte-profil.pdf?${q}`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setExp(x => ({ ...x, en_cours: false, erreur: r.status === 404 && !j.error ? 'L’export PDF arrive côté serveur.' : (j.error || `Erreur ${r.status}`) }));
        return;
      }
      const nom = (/filename="([^"]+)"/.exec(r.headers.get('content-disposition') || '') || [])[1] || 'Carte_profil.pdf';
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = nom; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      // Photo demandée mais absente du PDF : le serveur dit pourquoi — on le
      // répète, le panneau reste ouvert pour que la phrase se lise.
      const sansPhoto = r.headers.get('x-carte-sans-photo');
      if (photoExport && sansPhoto) {
        let raison = sansPhoto;
        try { raison = decodeURIComponent(sansPhoto); } catch { /* tel quel */ }
        setExp(x => ({ ...x, en_cours: false, info: `PDF téléchargé sans la photo : ${raison === '1' ? 'elle n’a pas pu être ajoutée' : raison}.` }));
      } else setExp(x => ({ ...x, en_cours: false, ouvert: false, info: null }));
    } catch {
      setExp(x => ({ ...x, en_cours: false, erreur: 'Le serveur ne répond pas. Réessayez dans un instant.' }));
    }
  };

  useEffect(() => {
    let annule = false;
    setEtat(e => ({ ...e, charge: true, erreur: null }));
    const q = besoinId ? `?besoin_id=${encodeURIComponent(besoinId)}` : '';
    fetch(`${BASE}/candidates/${candidatId}/carte-profil${q}`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } })
      .then(async r => {
        const j = await r.json().catch(() => ({}));
        if (annule) return;
        if (r.status === 404 && !j.error) setEtat({ charge: false, data: null, erreur: 'La carte profil arrive côté serveur.' });
        else if (!r.ok) setEtat({ charge: false, data: null, erreur: j.error || `Erreur ${r.status}` });
        else setEtat({ charge: false, data: j.data || j, erreur: null });
      })
      .catch(() => { if (!annule) setEtat({ charge: false, data: null, erreur: 'Le serveur ne répond pas. Réessayez dans un instant.' }); });
    return () => { annule = true; };
  }, [candidatId, besoinId, rechargement]);

  const d = etat.data;
  useEffect(() => { setPhotoCassee(false); }, [d?.identite?.photo_preview_url]);

  if (etat.charge && !d) {
    return <div data-da="vue" className="cp cp-etat"><i className="bi bi-arrow-repeat cp-tourne" aria-hidden="true" /> Chargement de la carte profil…</div>;
  }
  if (etat.erreur) {
    return <div data-da="vue" className="cp cp-etat cp-etat--erreur"><i className="bi bi-exclamation-circle" aria-hidden="true" /> {etat.erreur}</div>;
  }
  if (!d) return null;

  const id = d.identite || {};
  const adq = d.adequation;
  const src = !photoCassee && photoSrc(id.photo_preview_url);
  const [prenom, ...reste] = String(id.nom_complet || '').split(' ');
  const ch = d.chiffres || {};
  const axes = Array.isArray(d.univers) ? d.univers : [];
  const generale = d.mode !== 'besoin';
  // Seul un besoin SANS CV format entreprise bloque la carte (03.11) ; un CV
  // non relu ou changé depuis sa relecture sert tel quel.
  const cvBloque = !generale && !!d.cv_du_besoin && (d.cv_du_besoin.utilisable === false
    || (d.cv_du_besoin.utilisable == null && !d.cv_du_besoin.document && !d.cv_du_besoin.valide));
  const positions = d.positionnements || [];
  const nomExport = generale ? exp.nom : (d.besoin?.nom_profils || 'complet');
  const peutRecalculer = !generale && !!d.besoin?.ao_id && (!adq || /texte du CV|matching/i.test(d.message || ''));

  return (
    <div data-da="vue" className="cp">
      <div className="cp-besoin">
        <div className="cp-modes" role="tablist" aria-label="Carte">
          <button type="button" role="tab" aria-selected={generale} className={generale ? 'is-actif' : ''} onClick={() => setBesoinId(null)} disabled={etat.charge}>
            <i className="bi bi-person-vcard" aria-hidden="true" /> Carte générale
          </button>
          <button type="button" role="tab" aria-selected={!generale} className={!generale ? 'is-actif' : ''}
            onClick={() => { if (generale && positions.length) setBesoinId(String(positions[0].besoin_id)); }} disabled={etat.charge || !positions.length}
            title={positions.length ? undefined : 'Ce profil n’est positionné sur aucun besoin'}>
            <i className="bi bi-intersect" aria-hidden="true" /> Pour un besoin
          </button>
        </div>
        {!generale && (
          <select className="erp-field" aria-label="Besoin" value={d.besoin?.besoin_id || ''} onChange={e => setBesoinId(e.target.value)} disabled={etat.charge}>
            {positions.map(p => (
              <option key={p.besoin_id} value={p.besoin_id}>
                {[p.titre, p.client].filter(Boolean).join(' — ')}{p.a_un_score ? '' : ' (pas encore de score)'}
              </option>
            ))}
          </select>
        )}
        {generale && !positions.length && <span className="cp-besoin-note">Positionnez ce profil sur un besoin pour obtenir sa carte face au poste.</span>}
        {etat.charge && <i className="bi bi-arrow-repeat cp-tourne" aria-hidden="true" />}
        <button type="button" className="da-btn da-btn--secondaire cp-exporter" aria-expanded={exp.ouvert}
          onClick={() => setExp(x => ({ ...x, ouvert: !x.ouvert, erreur: null }))}>
          <i className="bi bi-file-earmark-pdf" aria-hidden="true" /> Exporter (PDF)
        </button>
      </div>

      {exp.ouvert && (
        <div className="cp-export" role="group" aria-label="Exporter la carte en PDF pour le client">
          <div className="cp-export-texte">
            <strong>Une page à envoyer au client</strong>
            <span>Ni coordonnées, ni salaire, ni tarif, ni notes de l’équipe ; seules les compétences démontrées y figurent.</span>
          </div>
          <label className="cp-export-option">
            <input type="checkbox" checked={!!id.photo_preview_url && photoExport} disabled={!id.photo_preview_url} onChange={e => setExp(x => ({ ...x, photo: e.target.checked }))} />
            <span>Inclure la photo{!id.photo_preview_url && <em>aucune photo sur la fiche</em>}</span>
          </label>
          <div className="cp-export-option" role="radiogroup" aria-label="Nom affiché">
            <label><input type="radio" name="cp-nom" checked={nomExport === 'complet'} onChange={() => choisirNom('complet')} /> Nom complet</label>
            <label><input type="radio" name="cp-nom" checked={nomExport === 'court'} onChange={() => choisirNom('court')} /> Prénom et initiale</label>
            {!generale && <em className="cp-export-regle">règle du besoin : vaut pour tous ses exports</em>}
          </div>
          {/* 03.11 : la relecture du CV ne bloque plus la carte ; seul un besoin
              sans CV format entreprise la bloque. */}
          {cvBloque && (
            <span className="cp-generer-erreur" role="status">Pas encore de CV format entreprise pour ce besoin : la carte destinée au client part depuis lui — générez-le d’abord.</span>
          )}
          {!generale && adq?.score != null && (
            <label className="cp-export-option">
              <input type="checkbox" checked={exp.adequation} onChange={e => setExp(x => ({ ...x, adequation: e.target.checked }))} />
              <span>Afficher l’adéquation au poste ({Math.round(adq.score)} %)</span>
            </label>
          )}
          <button type="button" className="da-btn da-btn--primaire" onClick={exporter} disabled={exp.en_cours || cvBloque}>
            {exp.en_cours ? <><i className="bi bi-arrow-repeat cp-tourne" aria-hidden="true" /> Préparation du PDF…</> : <><i className="bi bi-download" aria-hidden="true" /> Télécharger le PDF</>}
          </button>
          {exp.erreur && <span className="cp-generer-erreur" role="alert">{exp.erreur}</span>}
          {exp.info && <span className="cp-export-info" role="status">{exp.info}</span>}
        </div>
      )}

      <div className="cp-grille">
        {/* Héros : la photo, l'identité, l'adéquation */}
        <section className="cp-heros" aria-label="Identité">
          {src
            ? <img className="cp-heros-photo" src={src} alt="" onError={() => setPhotoCassee(true)} />
            : <div className="cp-heros-initiales" aria-hidden="true">{initiales(prenom, reste.join(' '))}</div>}
          <div className="cp-heros-voile" />
          <div className="cp-heros-contenu">
            <h2 className="cp-nom">{id.nom_complet || 'Candidat'}</h2>
            {id.titre && <div className="cp-titre">{id.titre}</div>}
            {generale && id.accroche && <p className="cp-accroche">{id.accroche}</p>}
            {!generale && d.argumentaire?.accroche && <p className="cp-accroche">{d.argumentaire.accroche}</p>}
            <div className="cp-meta">
              {id.localisation && <span><i className="bi bi-geo-alt" aria-hidden="true" /> {id.localisation}</span>}
              {Number(id.annees_experience) > 0 && <span><i className="bi bi-briefcase" aria-hidden="true" /> {id.annees_experience} ans d’expérience</span>}
            </div>
            {(id.competences || []).length > 0 && (
              <div className="cp-chips">{id.competences.map(k => <span key={k} className="cp-chip">{k}</span>)}</div>
            )}
            <div className="cp-heros-jauge">
              {generale
                ? <div className="cp-heros-besoin">
                    <span>Carte générale</span>
                    <strong>{positions.length ? `Positionné sur ${positions.length} besoin${positions.length > 1 ? 's' : ''}` : 'Positionné sur aucun besoin'}</strong>
                    <em>L’adéquation se lit face à un besoin : choisissez « Pour un besoin ».</em>
                  </div>
                : adq && adq.score != null
                ? <><JaugeArc valeur={Math.round(adq.score)} />
                    <div className="cp-heros-besoin">
                      <span>Adéquation au besoin</span>
                      <strong>{d.besoin?.titre || ''}</strong>
                      {adq.potentiel != null && Math.round(adq.potentiel) > Math.round(adq.score) && <em>Potentiel {Math.round(adq.potentiel)} % une fois les points confirmés</em>}
                    </div></>
                : <div className="cp-heros-besoin"><span>Adéquation au besoin</span><em>{d.message || 'Pas encore de score.'}</em>
                    {peutRecalculer && (
                      <button type="button" className="da-btn da-btn--primaire cp-recalculer" onClick={recalculer} disabled={recalcul.en_cours}>
                        {recalcul.en_cours ? <><i className="bi bi-arrow-repeat cp-tourne" aria-hidden="true" /> Recalcul…</> : <><i className="bi bi-arrow-repeat" aria-hidden="true" /> Recalculer le score</>}
                      </button>
                    )}
                    {recalcul.message && <em>{recalcul.message}</em>}
                  </div>}
            </div>
          </div>
        </section>

        {/* Univers de compétences */}
        <section className="cp-carte cp-univers" aria-label="Univers de compétences">
          <header className="cp-carte-tete">
            <h3>Univers de compétences</h3>
            {adq && <span className="cp-mono">{adq.demontrees}/{adq.exigences} exigences démontrées</span>}
            {generale && axes.length > 0 && <span className="cp-mono">{d.analyse ? 'domaines lus par Leman · durées calculées sur les missions' : 'compétences relevées mot à mot'}</span>}
          </header>
          {generale && d.generation && (
            <div className={`cp-generer${d.analyse && !d.analyse.a_jour ? ' cp-generer--perime' : ''}${d.analyse?.a_jour ? ' cp-generer--discret' : ''}`}>
              <div className="cp-generer-texte">
                {d.analyse?.a_jour
                  ? <span className="cp-mono">Générée le {fmtMoisAn(d.analyse.genere_le)}{d.analyse.genere_par ? ` par ${d.analyse.genere_par}` : ''}{d.analyse.simulee ? ' (simulée)' : ''}</span>
                  : <span>{d.analyse ? 'Le CV a changé depuis la dernière génération.' : d.generation.aide}</span>}
                {gen.erreur && <span className="cp-generer-erreur" role="alert">{gen.erreur}</span>}
              </div>
              <button type="button" className={`da-btn ${d.analyse?.a_jour ? 'da-btn--secondaire' : 'da-btn--primaire'}`}
                onClick={generer} disabled={gen.en_cours || !d.generation.possible}
                title={d.generation.possible ? undefined : 'Déposez d’abord le CV de ce profil'}>
                {gen.en_cours
                  ? <><i className="bi bi-arrow-repeat cp-tourne" aria-hidden="true" /> Leman lit le CV…</>
                  : <><i className="bi bi-person-lines-fill" aria-hidden="true" /> {d.generation.libelle}</>}
              </button>
            </div>
          )}
          {axes.length >= 3
            ? <Radar axes={axes} />
            : axes.length > 0
              ? <ul className="cp-barres">{axes.map(a => (
                  <li key={a.exigence} title={a.exigence}>
                    <span>{a.libelle}</span>
                    <span className="cp-barre"><span style={{ width: `${a.niveau}%` }} /></span>
                    <span className={`cp-palier cp-palier--${a.palier}`}>{a.palier_libelle}{a.valeur_lisible && a.valeur_lisible !== String(a.niveau) ? ` · ${a.valeur_lisible}` : ''}</span>
                  </li>))}
                </ul>
              : <p className="cp-vide">{d.message || 'Les exigences du besoin apparaîtront ici une fois le profil scoré.'}</p>}
          {axes.length > 0 && (
            <div className="cp-legende">
              {(d.paliers || []).filter(p => p.cle !== 'a_demontrer').map(p => (
                <span key={p.cle}><span className={`cp-pastille cp-pastille--${p.cle}`} />{p.libelle} {p.seuil || `≥ ${p.min}`}</span>
              ))}
            </div>
          )}
        </section>

        {/* Colonne droite : points clés + expérience */}
        <div className="cp-droite">
          <section className="cp-carte cp-points" aria-label={!generale && d.argumentaire ? 'Pourquoi ce profil' : 'Points clés'}>
            <header className="cp-carte-tete"><h3>{!generale && d.argumentaire ? 'Pourquoi ce profil' : 'Points clés'}</h3></header>
            {!generale && d.generation && (
              <div className={`cp-generer cp-generer--points${d.argumentaire && !d.argumentaire.a_jour ? ' cp-generer--perime' : ''}${d.argumentaire?.a_jour ? ' cp-generer--discret' : ''}`}>
                <div className="cp-generer-texte">
                  {d.argumentaire?.a_jour
                    ? <span className="cp-mono">Rédigé le {fmtMoisAn(d.argumentaire.genere_le)}{d.argumentaire.genere_par ? ` par ${d.argumentaire.genere_par}` : ''}{d.argumentaire.simulee ? ' (simulé)' : ''}</span>
                    : <span>{d.argumentaire ? 'Le score a changé depuis la rédaction.' : d.generation.aide}</span>}
                  {gen.erreur && <span className="cp-generer-erreur" role="alert">{gen.erreur}</span>}
                </div>
                <button type="button" className={`da-btn ${d.argumentaire?.a_jour || cvBloque ? 'da-btn--secondaire' : 'da-btn--primaire'}`}
                  onClick={generer} disabled={gen.en_cours || !d.generation.possible}>
                  {gen.en_cours
                    ? <><i className="bi bi-arrow-repeat cp-tourne" aria-hidden="true" /> Leman rédige…</>
                    : <><i className="bi bi-chat-square-text" aria-hidden="true" /> {d.generation.libelle}</>}
                </button>
              </div>
            )}
            {!generale && d.argumentaire?.arguments?.length
              ? <ul>{d.argumentaire.arguments.map(a => (
                  <li key={a.texte}>
                    <i className="bi bi-check-circle-fill" aria-hidden="true" />
                    <div>
                      <div className="cp-point-libelle">{a.texte}</div>
                      {(a.exigences || []).length > 0 && <div className="cp-point-preuve">{a.exigences.join(' · ')}</div>}
                    </div>
                  </li>))}
                </ul>
            : (d.points_cles || []).length
              ? <ul>{d.points_cles.map(p => (
                  <li key={p.libelle}>
                    <i className="bi bi-check-circle-fill" aria-hidden="true" />
                    <div>
                      <div className="cp-point-libelle">{p.libelle}</div>
                      {p.preuve && <div className="cp-point-preuve">{p.preuve}</div>}
                      {p.confirme_par_candidat && <div className="cp-point-preuve">Confirmé par le candidat</div>}
                    </div>
                  </li>))}
                </ul>
              : <p className="cp-vide">{generale ? 'Aucune compétence pratiquée en mission depuis un an ou plus.' : 'Aucune exigence encore démontrée à 75 ou plus.'}</p>}
          </section>

          <section className="cp-carte cp-exp" aria-label="Expérience récente">
            <header className="cp-carte-tete">
              <h3>Expérience récente</h3>
              {d.experiences_source && <span className="cp-mono">{SOURCES_CV[d.experiences_source] || 'CV de la fiche'}</span>}
            </header>
            {(d.experiences || []).length
              ? <ol>{d.experiences.map((x, i) => (
                  <li key={i}>
                    <div className="cp-exp-tete">
                      <strong>{x.employeur || x.poste || 'Mission'}</strong>
                      {x.periode && <span className="cp-mono">{x.periode}</span>}
                    </div>
                    {x.employeur && x.poste && <div className="cp-exp-poste">{x.poste}</div>}
                    {x.resume && <p>{x.resume}</p>}
                  </li>))}
                </ol>
              : <p className="cp-vide">Aucune mission lue dans le CV.</p>}
          </section>
        </div>
      </div>

      {/* Bande de chiffres */}
      <div className="cp-bande">
        {generale
          ? <div><i className="bi bi-intersect" aria-hidden="true" /><span>Besoins</span><strong>{ch.besoins ?? positions.length}</strong></div>
          : <div><i className="bi bi-intersect" aria-hidden="true" /><span>Adéquation</span><strong>{ch.adequation != null ? `${Math.round(ch.adequation)} %` : '—'}</strong></div>}
        <div><i className="bi bi-layers" aria-hidden="true" /><span>Domaines clés</span><strong>{axes.length ? ch.domaines_cles : '—'}</strong></div>
        <div><i className="bi bi-briefcase" aria-hidden="true" /><span>Expérience</span><strong>{Number(ch.annees_experience) > 0 ? `${ch.annees_experience} ans` : '—'}</strong></div>
        <div><i className="bi bi-geo-alt" aria-hidden="true" /><span>Localisation</span><strong>{ch.localisation || '—'}</strong></div>
        {onDetail && <button type="button" className="da-btn da-btn--primaire cp-detail" onClick={onDetail}>Voir le détail complet <i className="bi bi-arrow-right" aria-hidden="true" /></button>}
      </div>
      {adq?.calcule_le && <div className="cp-pied cp-mono">Score calculé le {fmtMoisAn(adq.calcule_le)}</div>}
    </div>
  );
}
